import React from 'react'
import { supabase } from '@/lib/supabase'
import { applyOptimisticReactionToggle } from '@/shared/utils/reactions'
import {
  addVisionMessageAttachments,
  fetchVisionCommunityThread,
  postVisionCommunityMessage,
  toggleVisionCardReaction,
  toggleVisionMessageReaction,
  deleteVisionCommunityMessage,
  updateVisionCommunityMessage,
} from '../services/visionCommunityService'
import type { CommunityThread } from '@/modules/comunidade/services/communityService'
import { uploadPublicFile } from '@/modules/upload/services/uploadService'
import { validateCommunityAttachmentFile } from '@/modules/comunidade/utils/communityAttachments'
import {
  appendEditedSuffixToHtml,
  prepareCommunityMessageContent,
  stripEditedSuffixFromHtml,
} from '@/modules/comunidade/utils/communityMessageContent'
import { emitCommunityThreadNotificationsAutoEnabled } from '@/modules/comunidade/services/communityThreadNotificationsEvents'

export function useVisionCommunityThread(
  visionId: number | null,
  currentUser: { name: string; avatarUrl?: string | null } | null,
  active: boolean,
) {
  const [loading, setLoading] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const [loadedOnce, setLoadedOnce] = React.useState(false)
  const [thread, setThread] = React.useState<CommunityThread>({
    messages: [],
    cardReactions: [],
  })

  const load = React.useCallback(async () => {
    if (!visionId || !active) {
      setThread({ messages: [], cardReactions: [] })
      setLoadedOnce(false)
      return
    }
    setLoading(true)
    try {
      const data = await fetchVisionCommunityThread(visionId)
      setThread(data)
      setLoadedOnce(true)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useVisionCommunityThread load error', error)
    } finally {
      setLoading(false)
    }
  }, [active, visionId])

  React.useEffect(() => {
    if (!visionId || !active) {
      setThread({ messages: [], cardReactions: [] })
      setLoadedOnce(false)
      return
    }
    let alive = true
    ;(async () => {
      await load()
      if (!alive) return
    })()
    return () => {
      alive = false
    }
  }, [active, load, visionId])

  React.useEffect(() => {
    if (!visionId || !active) return

    let timer: any = null

    const scheduleReload = () => {
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
      timer = setTimeout(() => {
        load().catch(() => {
          // erros tratados dentro de load
        })
      }, 500)
    }

    const channel = supabase
      .channel(`community_vision_thread:${visionId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'vision_community_message_reactions' },
        () => scheduleReload(),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vision_community_reactions',
          filter: `vision_id=eq.${visionId}`,
        } as any,
        () => scheduleReload(),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vision_community_messages',
          filter: `vision_id=eq.${visionId}`,
        } as any,
        () => scheduleReload(),
      )
      .subscribe()

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // ignore
      }
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
    }
  }, [active, load, visionId])

  React.useEffect(() => {
    if (!visionId || !active) return

    const interval = setInterval(() => {
      load().catch(() => {
        // silencioso; proximo ciclo corrige
      })
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [active, load, visionId])

  const sendMessage = React.useCallback(
    async (content: string, attachments?: File[]) => {
      if (!visionId || !currentUser || !active) return

      const prepared = prepareCommunityMessageContent(String(content || ''))
      const html = prepared.html
      const files = (Array.isArray(attachments) ? attachments : [])
        .filter((file): file is File => !!file)
        .filter((file) => validateCommunityAttachmentFile(file).ok)

      if (!prepared.text && !files.length) return

      setSending(true)
      try {
        const messageId = await postVisionCommunityMessage(visionId, currentUser, html)
        if (messageId) {
          emitCommunityThreadNotificationsAutoEnabled({
            contextType: 'vision',
            contextId: visionId,
          })
        }
        if (messageId && files.length) {
          const uploads = await Promise.all(
            files.map(async (file) => {
              const { url, bucket, path } = await uploadPublicFile(
                file,
                `vision-community/messages/${messageId}`,
              )
              return {
                url,
                storageBucket: bucket,
                storagePath: path,
                contentType: file.type || null,
                sizeBytes: file.size,
              }
            }),
          )
          await addVisionMessageAttachments(messageId, uploads)
        }
        await load()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('send vision community message error', e)
      } finally {
        setSending(false)
      }
    },
    [active, visionId, currentUser, load],
  )

  const toggleMessageEmoji = React.useCallback(
    async (messageId: number, emoji: string) => {
      if (!visionId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      const id = Number(messageId)
      if (!Number.isFinite(id) || id <= 0) return

      setThread((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          Number(msg.id) === id
            ? {
                ...msg,
                reactions: applyOptimisticReactionToggle(msg.reactions, trimmed),
              }
            : msg,
        ),
      }))

      void toggleVisionMessageReaction(id, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('useVisionCommunityThread toggleMessageEmoji error', error)
        load().catch(() => {
          // erro tratado dentro de load
        })
      })
    },
    [active, visionId, load],
  )

  const toggleCardEmoji = React.useCallback(
    async (emoji: string) => {
      if (!visionId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      setThread((prev) => ({
        ...prev,
        cardReactions: applyOptimisticReactionToggle(prev.cardReactions, trimmed),
      }))

      void toggleVisionCardReaction(visionId, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('useVisionCommunityThread toggleCardEmoji error', error)
        load().catch(() => {
          // erro tratado dentro de load
        })
      })
    },
    [active, visionId, load],
  )

  const deleteMessage = React.useCallback(
    async (messageId: number) => {
      if (!visionId || !active) return
      const id = Number(messageId)
      if (!Number.isFinite(id) || id <= 0) return

      setThread((prev) => ({
        ...prev,
        messages: prev.messages.filter((msg) => Number(msg.id) !== id),
      }))

      try {
        await deleteVisionCommunityMessage(id)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('useVisionCommunityThread deleteMessage error', error)
        load().catch(() => {
          // erro tratado dentro de load
        })
      }
    },
    [active, visionId, load],
  )

  const editMessage = React.useCallback(
    async (messageId: number, content: string) => {
      if (!visionId || !active) return

      const prepared = prepareCommunityMessageContent(String(content || ''))
      if (!prepared.text) return

      const baseHtml = stripEditedSuffixFromHtml(prepared.html).html
      const finalHtml = appendEditedSuffixToHtml(baseHtml)
      if (!finalHtml) return

      try {
        const updated = await updateVisionCommunityMessage(messageId, finalHtml)
        if (!updated) {
          await load()
          return
        }

        setThread((prev) => ({
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.id === messageId ? { ...msg, content: updated } : msg,
          ),
        }))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('useVisionCommunityThread editMessage error', error)
        await load()
      }
    },
    [active, visionId, load],
  )

  return {
    thread,
    loading,
    loadedOnce,
    sending,
    reload: load,
    sendMessage,
    toggleMessageEmoji,
    toggleCardEmoji,
    deleteMessage,
    editMessage,
  }
}
